import React, { Suspense, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { observer } from "mobx-react-lite";
import { Spin } from "antd";

// Stores
import authStore from "./stores/authStore";
import cartStore from "./stores/cartStore";

// Layout
import MainLayout from "./components/layout/MainLayout";

import { initPixel, pageView } from "./utils/pixel";
import {
  initTikTokPixel,
  pageView as tikTokPageView,
} from "./utils/tiktokPixel";

// ── تأجيل تحميل البكسلات لحد ما الصفحة تخلص ─────────────────────────────
// false (الافتراضي الآمن): البكسلات بتشتغل زي الأول بالظبط.
// true: البكسلات بتتحمّل بعد اكتمال الصفحة وفي وقت فراغ المتصفح (أخف على TBT).
//
// ⚠️ غيّرها لـ true بعد ما تراجع utils/pixel.js و utils/tiktokPixel.js،
// لأن أحداث زي ViewContent و AddToCart ممكن تتبعت قبل ما البكسل يجهز وتضيع.
const DEFER_PIXELS = false;

const runWhenIdle = (cb) => {
  const run = () =>
    "requestIdleCallback" in window
      ? window.requestIdleCallback(cb, { timeout: 4000 })
      : setTimeout(cb, 2500);

  if (document.readyState === "complete") run();
  else window.addEventListener("load", run, { once: true });
};

// Pages (lazy loaded - كل صفحة بتتحمل بشكل منفصل)
const HomePage = React.lazy(() => import("./pages/Home/HomePage"));
const ProductListPage = React.lazy(() =>
  import("./pages/Product/ProductListPage")
);
const ProductDetailPage = React.lazy(() =>
  import("./pages/Product/ProductDetailPage")
);

// السلة والدفع: بيتحمّلوا في الخلفية وقت الفراغ، عشان لما العميل يضغط "شراء"
// يلاقيهم جاهزين من غير انتظار (ما بيأثرش على التحميل الأول)
const CartPage = React.lazy(() =>
  import(/* webpackPrefetch: true */ "./pages/Cart/CartPage")
);
const CheckoutPage = React.lazy(() =>
  import(/* webpackPrefetch: true */ "./pages/Checkout/CheckoutPage")
);
const OrderSuccessPage = React.lazy(() =>
  import("./pages/Orders/OrderSuccessPage")
);
const MyOrdersPage = React.lazy(() => import("./pages/Orders/MyOrdersPage"));
const OrderDetailPage = React.lazy(() =>
  import("./pages/Orders/OrderDetailPage")
);
const LoginPage = React.lazy(() => import("./pages/Auth/LoginPage"));
const RegisterPage = React.lazy(() => import("./pages/Auth/RegisterPage"));
const AccountPage = React.lazy(() => import("./pages/Account/AccountPage"));
const NotFoundPage = React.lazy(() => import("./pages/NotFoundPage"));
const AboutUs = React.lazy(() => import("./pages/AboutUs"));

// شاشة تحميل بسيطة تظهر أثناء تحميل أي صفحة lazy
const PageLoader = () => (
  <div
    className="flex justify-center items-center"
    style={{ minHeight: "60vh" }}
  >
    <Spin size="large" />
  </div>
);

// ← تتبع تغيير الصفحات + ScrollToTop
const PixelPageTracker = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);

    // Facebook PageView
    pageView();

    // TikTok PageView
    tikTokPageView();
  }, [location.pathname]);

  return null;
};

// Protected Route للصفحات اللي تحتاج login
const ProtectedRoute = observer(({ children }) => {
  if (!authStore.isHydrated) return null;
  if (!authStore.isLoggedIn) {
    return <Navigate to="/login" replace />;
  }
  return children;
});

// Guest Route (login/register) → لو مسجل روّح للـ home
const GuestRoute = observer(({ children }) => {
  if (!authStore.isHydrated) return null;
  if (authStore.isLoggedIn) {
    return <Navigate to="/" replace />;
  }
  return children;
});

const App = observer(() => {
  useEffect(() => {
    cartStore.fetchCart();

    const initPixels = () => {
      // Facebook Pixel
      initPixel();

      // TikTok Pixel
      initTikTokPixel();
    };

    if (DEFER_PIXELS) runWhenIdle(initPixels);
    else initPixels();
  }, []);

  return (
    <BrowserRouter>
      <PixelPageTracker />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<MainLayout />}>
            {/* Public */}
            <Route index element={<HomePage />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="products" element={<ProductListPage />} />
            <Route path="products/:slug" element={<ProductDetailPage />} />
            <Route path="cart" element={<CartPage />} />

            {/* Checkout (Guest + User) */}
            <Route path="checkout" element={<CheckoutPage />} />
            <Route
              path="order-success/:orderNumber"
              element={<OrderSuccessPage />}
            />

            {/* Order tracking (Guest بـ order number) */}
            <Route path="orders/:orderNumber" element={<OrderDetailPage />} />

            {/* Protected */}
            <Route
              path="my-orders"
              element={
                <ProtectedRoute>
                  <MyOrdersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="account"
              element={
                <ProtectedRoute>
                  <AccountPage />
                </ProtectedRoute>
              }
            />

            {/* Auth */}
            <Route
              path="login"
              element={
                <GuestRoute>
                  <LoginPage />
                </GuestRoute>
              }
            />
            <Route
              path="register"
              element={
                <GuestRoute>
                  <RegisterPage />
                </GuestRoute>
              }
            />

            {/* 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
});

export default App;